import { forEach, has, isEmpty, isNil, slice } from 'lodash';
import flowTypes from 'new/components/flows/types/flowTypes';
import { ReactComponent as OutputSamplesIcon } from 'new/components/icons/arrow-output-samples.svg';
import DataSetSplitTest from 'new/components/SplitDataSet/DataSetSplitTest';
import { Tabs } from 'new/elements/Tabs';
import React, { useEffect, useMemo } from 'react';
import Joyride, { STATUS } from 'react-joyride';
import { useSelector } from 'react-redux';
import { PRODUCT_TOURS_ENABLED } from '../../constants/config';
import {
  PRODUCT_TOURS_KEYS,
  PRODUCT_TOURS_STYLES,
  USER_SETTINGS_KEYS,
} from '../../constants/productTours';
import {
  SCHEMA_DISPLAY_LIVE_SAMPLES_DEFAULT,
  SCHEMA_DISPLAY_NUM_SAMPLES_DEFAULT,
  SchemaBrowserType,
  SchemaEditorViewModeType,
} from '../../constants/schemaEditor';
import { SUPPORT_KEY_DATASET_FORM_TX } from '../../constants/supportPageKeys';
import { ACLQueryModeTypes } from '../../constants/visibilityFilters';
import DataSamplesProcessor from '../../lib/DataSamplesProcessor';
import JSONUtils from '../../lib/JSONUtils';
import { getSemanticSchema } from 'new/utils/DataSetUtil/getSemanticSchema';
import ResizableHorizontalLayout from '../elements/ResizableHorizontalLayout';
import ConfigureRecipe from './ConfigureRecipe';
import InputSamplesIcon from './icons/InputSamplesIcon';
import RecipeBuilderHeader from './RecipeBuilderHeader';
import RecipeBuilderSamples from './RecipeBuilderSamples';
import SchemaSpecSelector from './SchemaSpecSelector';

import './RecipeBuilder.scss';

const DESIGNER_TOUR_STEP = [
  {
    content: (
      <div>
        <p>
          Creating and managing Nexsets is even easier now! Check out this quick walkthrough of the
          new screen.
        </p>
      </div>
    ),
    placement: 'center',
    target: '.recipe-builder-body',
    title: '✨ New Nexset Designer ✨',
  },
  {
    content: <p>Set or update the name of this Nexset.</p>,
    placement: 'bottom',
    target: '.recipe-builder-header .nexset-name',
    title: 'Nexset Name',
  },
  {
    content: (
      <div>
        <p>View samples and schema of data coming into the Nexset</p>
        <p>
          You can also <span style={PRODUCT_TOURS_STYLES.stepEmphasis}>edit</span> this panel to
          simulate input data and test Nexset rules.
        </p>
      </div>
    ),
    placement: 'right',
    target: '.recipe-builder-samples-container.input-samples',
    title: 'Nexset Input',
  },
  {
    content: (
      <div>
        <p>Set up the rules for modifying incoming data to produce the desired Nexset output.</p>
        <p>
          Transformations, validations, filters, and more: choose the type of{' '}
          <span style={PRODUCT_TOURS_STYLES.stepEmphasis}>Rule Group</span> and and set the{' '}
          <span style={PRODUCT_TOURS_STYLES.stepEmphasis}>Rules</span> in the exact order in which
          they should be applied. Watch our tutorial videos for details on each Rule Group.
        </p>
      </div>
    ),
    placement: 'auto',
    spotlightClicks: false,
    target: '.configure-recipe .configure-recipe-body',
    title: 'Nexset Rules',
  },
  {
    content: (
      <div>
        <p>
          View how these <span style={PRODUCT_TOURS_STYLES.stepEmphasis}>Nexset Rules</span> will
          modify the current <span style={PRODUCT_TOURS_STYLES.stepEmphasis}>Input Samples</span> to
          generate <b>Output Samples and Schema</b>.
        </p>
        <p>
          Note that these samples are only a{' '}
          <span style={PRODUCT_TOURS_STYLES.stepEmphasis}>simulation of expected </span> Nexset
          output schema.
        </p>
      </div>
    ),
    placement: 'left',
    target: '.recipe-builder-samples-container.output-samples',
    title: 'Nexset Output',
  },
  {
    content: (
      <div>
        <p>Select and apply recommended rules to quickly set desired Nexset Rules.</p>
        <p>
          These recommendations change based on your attribute selections. As they are just
          recommendations, you can always edit the generated Rules, or you can ignore them
          completely and set your own Rules.
        </p>
      </div>
    ),
    placement: 'top',
    target: '.recipe-builder-recommendations',
    title: '✨ Recommendations ✨',
  },
  {
    content: (
      <div>
        <p>Need to comply with a Nexset schema specification?</p>
        <p>
          Select the <span style={PRODUCT_TOURS_STYLES.stepEmphasis}>desired Schema Template</span>{' '}
          and use generated Recommendations to set necessary Rules. Easily visualize what has been
          mapped and what is missing.
        </p>
      </div>
    ),
    placement: 'top',
    target: '.recipe-builder-samples-container .recipe-builder-schema-spec-selector',
    title: 'Output Schema Templates',
  },
  {
    content: (
      <div>
        <p className="video">
          <iframe
            src="https://player.vimeo.com/video/779506762?h=ea17d05d06"
            width="500"
            height="327"
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen=""
          ></iframe>
        </p>
        <p className="title">
          Check out this short video to learn what has changed in this new version.
        </p>
        <p className="title">
          Click on the <span style={PRODUCT_TOURS_STYLES.stepEmphasis}>？</span> above, or head over
          to the <span style={PRODUCT_TOURS_STYLES.stepEmphasis}>Support → Videos</span> section
          anytime for more tutorials and guides.
        </p>
        <p></p>
      </div>
    ),
    placement: 'center',
    styles: {
      options: {
        width: 540,
        height: 400,
      },
    },
    target: '.recipe-builder-body',
    title: 'Nexset Designer Demo',
  },
];

class RecipeBuilder extends React.PureComponent {
  state = {
    numSamples: SCHEMA_DISPLAY_NUM_SAMPLES_DEFAULT,
    liveSamples: SCHEMA_DISPLAY_LIVE_SAMPLES_DEFAULT,
    selectedViewMode:
      this.props.dataSet?.flow_type === flowTypes.api_server
        ? SchemaEditorViewModeType.CODE
        : SchemaEditorViewModeType.TABLE,
    showMeta: false,
    customSamplesCode: null, // user-edited input samples code,
    awaitingUpdateSamples: false,
    selectedSplitOutputId: null,
    displayDesignerTour: false,
  };
  _recipeBuilderBody = React.createRef();

  componentDidMount() {
    this.props.loadDataMaps(false, ACLQueryModeTypes.MODE_COLLABORATOR.key);
    this.props.getAllSchemaSpecs(ACLQueryModeTypes.MODE_COLLABORATOR.key);

    this.props.setContextualSupport(SUPPORT_KEY_DATASET_FORM_TX);
  }

  componentWillUnmount() {
    this.props.resetSchemaEditor();
    this.props.resetRecipeGroups();
    this.props.unsetContextualSupport(SUPPORT_KEY_DATASET_FORM_TX);
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.dataSet) {
      if (this.props.isEditMode && !prevProps.inputFlatSchema && this.props.inputFlatSchema) {
        this.props.updateInputSchema(this.props.inputFlatSchema);
      }

      if (
        this.state.numSamples !== prevState.numSamples ||
        this.state.liveSamples !== prevState.liveSamples
      ) {
        this._fetchNewSamples(true);
      }
    }

    if (prevProps.isLoadingInitialData === true && this.props.isLoadingInitialData === false) {
      // we should have fetched user settings by now.
      const {
        dataSet,
        userSettings,
        addUserSettings,
        updateUserSettings,
        isImpersonationMode,
        isLoadingUserSettings,
      } = this.props;

      let shouldDisplayTour = false;
      let settingId;

      if (isImpersonationMode === false && isLoadingUserSettings === false) {
        //check if user has not already seen this tour.
        if (isNil(userSettings) || isEmpty(userSettings)) {
          //never set user settings.
          shouldDisplayTour = true;
        } else {
          //user has set some webapp settings. lets check if tour is needed.

          forEach(userSettings, (s) => {
            if (s.user_settings_type === USER_SETTINGS_KEYS.nexla_web_app) {
              settingId = s.id;
              shouldDisplayTour =
                !has(s.settings, PRODUCT_TOURS_KEYS.tour_seen_nx_designer_upgrade) ||
                s.settings[PRODUCT_TOURS_KEYS.tour_seen_nx_designer_upgrade] === false
                  ? true
                  : false;
              return false;
            }
          });
        }
      }

      if (shouldDisplayTour === true) {
        this.setState(
          {
            displayDesignerTour: true,
          },
          () => {
            if (settingId) {
              // update existing userSettings
              updateUserSettings(settingId, {
                [PRODUCT_TOURS_KEYS.tour_seen_nx_designer_upgrade]: true,
              });
            } else {
              // no existing userSettings, create userSettings
              addUserSettings(USER_SETTINGS_KEYS.nexla_web_app, {
                [PRODUCT_TOURS_KEYS.tour_seen_nx_designer_upgrade]: true,
              });
            }
          },
        );
      }

      if (
        dataSet?.flow_type === flowTypes.api_server &&
        this.state.selectedViewMode === SchemaEditorViewModeType.TABLE
      ) {
        this.setState({ selectedViewMode: SchemaEditorViewModeType.CODE });
      }
    }

    if (
      this.state.awaitingUpdateSamples &&
      this.props.fromSamples.fromUpdatedAt !== prevProps.fromSamples.fromUpdatedAt
    ) {
      const { recipeGroups, fromSamples, recipeApplied, applyRecipe } = this.props;

      this.setState({ awaitingUpdateSamples: false });

      this._handleResetSamplesClicked();
      applyRecipe(recipeGroups, fromSamples.fromNested);
    }
  }

  render() {
    const {
      dataSet,
      dataSetName,
      toDataSet,
      fromSchema,
      fromSamples,
      toSchema,
      toSamples,
      characteristics,
      isEditMode,
      isLoadingInitialData,
      isLoadingDataSet,
      isLoadingInSamples,
      onDataSetNameChanged,
      loadReusableTransforms,
      isSplitMode,
    } = this.props;
    const {
      numSamples,
      liveSamples,
      selectedViewMode,
      showMeta,
      customSamplesCode,
      selectedSplitOutputId,
    } = this.state;
    const darkEditor = false; //dataSet?.flow_type === flowTypes.api_server;

    return (
      <div className="recipe-builder">
        <RecipeBuilderHeader
          dataSet={dataSet}
          dataSetName={dataSetName}
          isSplitMode={isSplitMode}
          isLoadingSamples={isLoadingInSamples}
          numSamples={numSamples}
          liveSamples={liveSamples}
          selectedViewMode={selectedViewMode}
          showMeta={showMeta}
          onDataSetNameChanged={onDataSetNameChanged}
          onNumSamplesChanged={this._handleNumSamplesChanged}
          onViewModeChanged={this._handleViewModeChanged}
          onShowMetaChanged={this._handleShowMetaChanged}
          onRefreshSamples={this._handleRefreshSamples}
          onDownloadSamples={this._handleDownloadSamples}
        />
        <div className="recipe-builder-body" ref={this._recipeBuilderBody}>
          <ResizableHorizontalLayout
            initialSizes={['25%', '50%', '25%']}
            minSizes={[250, 556, 250]}
          >
            <div className="recipe-builder-samples-container input-samples">
              <RecipeBuilderSamples
                gridId={SchemaBrowserType.INPUT}
                headingText="Nexset Input"
                headingIcon={<InputSamplesIcon />}
                dataSet={dataSet}
                allowSelection
                allowEditing
                showMeta={showMeta}
                liveSamples={liveSamples}
                schema={fromSchema.flat}
                metaSchema={fromSchema.metaFlat}
                samples={fromSamples.fromFlat}
                semanticSchema={getSemanticSchema(dataSet?.semantic_schema)}
                nestedSamples={fromSamples.fromNested}
                samplesUpdatedAt={fromSamples.fromUpdatedAt}
                characteristics={characteristics}
                showCharacteristics
                customSamplesCode={customSamplesCode}
                selectedViewMode={selectedViewMode}
                darkEditor={darkEditor}
                isLoading={isLoadingInSamples || isLoadingDataSet}
                onCloseInfo={this._handleInfoClosed}
                onResetClicked={this._handleResetSamplesClicked}
                onSaveClicked={this._handleSaveSamplesClicked}
                onEditClicked={this._handleEditSamplesClicked}
                onEdit={this._handleSamplesEdited}
                onViewModeChanged={this._handleViewModeChanged}
                onShowMetaChanged={this._handleShowMetaChanged}
              />
            </div>
            <div>
              {isSplitMode ? (
                <DataSetSplitTest
                  parentDataSet={dataSet}
                  splitterDataSet={toDataSet}
                  customSamplesCode={customSamplesCode}
                  selectedViewMode={selectedViewMode}
                  showMeta={showMeta}
                />
              ) : (
                <ConfigureRecipe
                  dataSet={dataSet}
                  customSamplesCode={customSamplesCode}
                  selectedViewMode={selectedViewMode}
                  showMeta={showMeta}
                  isEditMode={isEditMode}
                  isLoadingInitialData={isLoadingInitialData}
                  loadReusableTransforms={loadReusableTransforms}
                />
              )}
            </div>
            <div className="recipe-builder-samples-container output-samples">
              {isSplitMode && (
                <SplitOutputSelector
                  nestedSamples={toSamples.toNested}
                  selectedSplitOutputId={selectedSplitOutputId}
                  onSelectedSplitOutputIdChanged={this._handleSelectedSplitOutputIdChanged}
                />
              )}
              <RecipeBuilderSamples
                gridId={SchemaBrowserType.OUTPUT}
                headingText="Nexset Output"
                headingIcon={<OutputSamplesIcon />}
                allowSelection
                showMeta={showMeta}
                schema={toSchema.flat}
                metaSchema={toSchema.metaFlat}
                dataSet={dataSet}
                samples={toSamples.toFlat}
                nestedSamples={toSamples.toNested}
                samplesUpdatedAt={toSamples.toUpdatedAt}
                showCharacteristics={false}
                selectedViewMode={selectedViewMode}
                selectedSplitOutputId={selectedSplitOutputId}
                isLoading={isLoadingDataSet}
                darkEditor={darkEditor}
                onCloseInfo={this._handleInfoClosed}
                hideExpandMetaToggle
              />
              {!isSplitMode && (
                <SchemaSpecSelector dataSet={dataSet} outputSchema={toSchema.flat} />
              )}
            </div>
          </ResizableHorizontalLayout>
        </div>
        {PRODUCT_TOURS_ENABLED === true && this.state.displayDesignerTour && (
          <Joyride
            // debug
            // disableOverlay
            disableScrolling
            disableScrollParentFix
            beaconComponent={null}
            callback={this._handleJoyrideCallback}
            continuous
            run={
              this.state.displayDesignerTour === true &&
              isLoadingInitialData === false &&
              isLoadingDataSet === false
            }
            steps={DESIGNER_TOUR_STEP}
            showProgresss
            showSkipButton
            styles={{
              options: {
                arrowColor: '#3A3C45', //'#fff',
                backgroundColor: '#3A3C45', //'#fff',
                beaconSize: 0,
                overlayColor: 'rgba(255, 255, 255, 0.75)', //'rgba(0, 0, 0, 0.5)',
                primaryColor: '#ff7436',
                spotlightShadow: '0 0 15px rgba(0, 0, 0, 0.5)',
                textColor: '#FAFAFA', //'#333',
                zIndex: 100,
              },
              tooltip: {
                borderRadius: 2,
              },
              tooltipTitle: {
                fontSize: 16,
                color: '#FAFAFA',
              },
              tooltipContent: {
                fontSize: 13,
              },
            }}
          />
        )}
      </div>
    );
  }

  _fetchNewSamples = (shouldApplyTransform = false) => {
    const { dataSet, recipeGroups, isEditMode, loadRecipeBuilderSamples } = this.props;
    const { numSamples, liveSamples } = this.state;

    loadRecipeBuilderSamples(
      dataSet.id,
      numSamples,
      liveSamples,
      recipeGroups,
      shouldApplyTransform,
      isEditMode,
    );
  };

  _clearError = () => {
    const { formType, clearFormError } = this.props;

    clearFormError(formType);
  };

  _handleViewModeChanged = (viewMode) => {
    this.setState({ selectedViewMode: viewMode });
  };

  _handleShowMetaChanged = (showMeta) => {
    this.setState({ showMeta });
  };

  _handleNumSamplesChanged = (optionValue) => {
    const { count, live } = optionValue;

    this.setState({
      numSamples: count,
      liveSamples: live,
    });
  };

  _handleRefreshSamples = () => {
    this._fetchNewSamples(true);
  };

  _handleDownloadSamples = (format) => {
    const { dataSet, toSchema, fromSchema, toSamples, fromSamples } = this.props;
    DataSamplesProcessor.downloadSamples(
      { input: fromSchema?.flat, output: toSchema?.flat },
      {
        input: { nested: fromSamples.fromNested, flat: fromSamples.fromFlat },
        output: { nested: toSamples.toNested, flat: toSamples.toFlat },
      },
      format,
      `nexset-${dataSet.id}-`,
    );
  };

  _handleEditSamplesClicked = () => {
    this.setState({ selectedViewMode: SchemaEditorViewModeType.CODE });
  };

  _handleSamplesEdited = (value) => {
    this._clearError();
    if (value === 'Loading...') return;
    this.setState({ customSamplesCode: value });
  };

  _handleResetSamplesClicked = () => {
    this._clearError();
    this.setState({ customSamplesCode: null });
  };

  _handleSaveSamplesClicked = () => {
    const {
      dataSet: { id },
      updateDataSamples,
    } = this.props;
    const { numSamples, customSamplesCode } = this.state;
    const samples = slice(JSONUtils.parseJson(customSamplesCode), 0, 5);

    this.setState({ awaitingUpdateSamples: true }, () =>
      updateDataSamples(id, samples, numSamples),
    );
  };

  _handleInfoClosed = () => {
    // a hack to fix the top part of recipe-builder-body getting clipped when
    // the info drawer is closed
    if (this._recipeBuilderBody) {
      setTimeout(() => this._recipeBuilderBody.current.scrollTo(0, 0), 0);
    }
  };

  _handleJoyrideCallback = (data) => {
    const { status } = data;
    const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

    if (finishedStatuses.includes(status)) {
      this.setState({ displayDesignerTour: false });
    }
  };

  _handleSelectedSplitOutputIdChanged = (selectedSplitOutputId) => {
    this.setState({ selectedSplitOutputId });
  };
}

const SplitOutputSelector = ({
  nestedSamples,
  selectedSplitOutputId,
  onSelectedSplitOutputIdChanged,
}) => {
  const lastTransform = useSelector((state) => state.splitter.lastTxPayload);

  const { outputIds, outputNamesById } = useMemo(() => {
    const splitterSpec = lastTransform?.transforms?.find(
      ({ operation }) => operation === 'nexla.splitter',
    )?.spec;

    // Get all configured output IDs from the splitter spec
    const configuredOutputIds = [];
    const names = {};

    if (splitterSpec?.rules) {
      splitterSpec.rules.forEach(({ destination }, index) => {
        if (destination?.id) {
          configuredOutputIds.push(destination.id);
          names[destination.id] = destination.name || `Output ${index + 1}`;
        }
      });
    }

    // Add fallback destination if configured
    if (splitterSpec?.fallback_destination?.id) {
      configuredOutputIds.push(splitterSpec.fallback_destination.id);
      names[splitterSpec.fallback_destination.id] = 'Fallback';
    }

    return {
      outputIds: configuredOutputIds,
      outputNamesById: names,
    };
  }, [lastTransform]);

  const selectedOutputId = selectedSplitOutputId || outputIds[0] || null;

  useEffect(() => {
    onSelectedSplitOutputIdChanged(selectedOutputId);
  }, [nestedSamples]);

  const handleChange = (e, value) => {
    onSelectedSplitOutputIdChanged(value);
  };

  if (outputIds.length === 0) {
    return null;
  }

  return (
    <div className="split-output-selector">
      <Tabs
        tabs={outputIds.map((id, index) => {
          const name = outputNamesById[id];
          if (name && name !== 'Fallback') {
            // For named outputs with IDs, show "ID: {ID} - {Name (trimmed to 6 chars)}"
            const trimmedName = name.length > 6 ? name.substring(0, 6) + '...' : name;
            return {
              label: `#${id}: ${trimmedName}`,
              value: id,
            };
          } else if (name === 'Fallback') {
            // For fallback, just show "Fallback"
            return {
              label: id ? `#${id}: Fallback` : 'Fallback',
              value: id,
            };
          } else {
            // For unnamed outputs, show "Output #{count}"
            return {
              label: `#${index + 1}`,
              value: id,
            };
          }
        })}
        value={selectedOutputId}
        onChange={handleChange}
        variant="scrollable"
        scrollButtons="auto"
      />
    </div>
  );
};

export default RecipeBuilder;
